import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Button, Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import { toast } from 'react-toastify';

import BZAPIService from '../../../services/BZAPIService';
import { VendorsContext } from '../../../context/VendorsContext';

const timeUnitOptions = [
  { label: 'Minutes', value: 'minute' },
  { label: 'Hours', value: 'hour' }
];

const mins = [
  { label: '15', value: '15' },
  { label: '30', value: '30' },
  { label: '45', value: '45' },
  { label: '60', value: '60' }
];

const hrs = Array.from({ length: 24 }, (_, i) => ({ label: String(i + 1), value: i + 1 }));

const daysOfWeek = [
  { label: 'Monday', value: 'MON' },
  { label: 'Tuesday', value: 'TUE' },
  { label: 'Wednesday', value: 'WED' },
  { label: 'Thursday', value: 'THU' },
  { label: 'Friday', value: 'FRI' },
  { label: 'Saturday', value: 'SAT' },
  { label: 'Sunday', value: 'SUN' }
];

const selectStyles = {
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    color: isFocused || isSelected ? 'white' : 'var(--ink)'
  }),
  menuPortal: base => ({ ...base, zIndex: 9999 })
};

const selectTheme = theme => ({
  ...theme,
  borderRadius: 5,
  colors: { ...theme.colors, primary25: '#253746', primary: '#bd472a' }
});

/**
 * Scheduling editor extracted from TabOne for use inside the wizard accordion.
 * Handles both interval and calendar scheduling modes.
 */
const ScheduleAccordionContent = ({
  vendorId,
  timeUnit,
  setTimeUnit,
  timeValue,
  setTimeValue,
  schedulingMode,
  setSchedulingMode,
  schedules,
  setSchedules,
  isConfigured,
  modalOpen,
  setModalOpen,
  setIsForceRun,
  onLoaded
}) => {
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(true);
  // eslint-disable-next-line
  const [vendors] = useContext(VendorsContext);
  const currentVendor = vendors?.find(v => v.id === vendorId);
  const isCloudVendor = currentVendor?.cloudVendor === 1;

  useEffect(() => {
    getScheduleSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId, vendors]);

  async function getScheduleSettings() {
    try {
      const res = await BZAPIService.getBugServiceSchedule(vendorId);
      const data = res?.data;

      const defaultUnit = isCloudVendor ? 'minute' : 'hour';
      const defaultValue = isCloudVendor ? mins[1] : hrs[3];

      if (data?.mode && data.rules && data.rules.length > 0) {
        const rule = data.rules[0];
        setSchedulingMode(data.mode);

        if (data.mode === 'interval') {
          let unit, number;

          if (rule.awsCron) {
            const cronParts = rule.awsCron
              .replace('cron(', '')
              .replace(')', '')
              .split(' ');
            const minutePart = cronParts[0];
            const hourPart = cronParts[1];

            if (hourPart.includes('*/')) {
              unit = 'hour';
              number = parseInt(hourPart.split('*/')[1]);
            } else if (hourPart === '*' && !minutePart.includes('*') && !minutePart.includes(',')) {
              unit = 'hour';
              number = 1;
            } else if (minutePart.includes('*/')) {
              unit = 'minute';
              number = parseInt(minutePart.split('*/')[1]);
            }
          }

          if (!unit && rule.schedule) {
            unit = rule.schedule.timeUnit || rule.schedule.unit;
            number = rule.schedule.number || rule.schedule.every;
            if (unit === 'hours') unit = 'hour';
            if (unit === 'minutes') unit = 'minute';
          }

          if (!unit && data.timeUnit) {
            unit = data.timeUnit;
            number = data.number;
          }

          if (!unit) unit = defaultUnit;

          setTimeUnit(unit);
          setTimeValue(number ? { label: number, value: number } : defaultValue);
        } else if (data.mode === 'calendar') {
          const parsedSchedules = data.rules.map(rule => {
            const { awsCron, nextRunUTC, name } = rule;
            const days = extractDaysFromCron(awsCron);
            const times = extractTimesFromCron(awsCron);
            return { days, times, nextRunUTC, name, awsCron };
          });
          setSchedules(parsedSchedules);
        }
      } else if (data?.timeUnit || data?.number) {
        setSchedulingMode('interval');
        setTimeUnit(data.timeUnit || defaultUnit);
        const val = data.number;
        setTimeValue(val ? { label: val, value: val } : defaultValue);
      } else {
        setSchedulingMode('interval');
        setTimeUnit(defaultUnit);
        setTimeValue(defaultValue);
      }
      setIsLoadingSchedule(false);
      onLoaded?.();
    } catch (e) {
      console.log(e);
      const defaultUnit = isCloudVendor ? 'minute' : 'hour';
      const defaultValue = isCloudVendor ? mins[1] : hrs[3];
      setTimeValue(defaultValue);
      setTimeUnit(defaultUnit);
      setSchedulingMode('interval');
      setIsLoadingSchedule(false);
      onLoaded?.();
    }
  }

  function extractDaysFromCron(awsCron) {
    const parts = awsCron
      .replace('cron(', '')
      .replace(')', '')
      .split(' ');
    if (parts.length >= 5) {
      const dayPart = parts[4];
      if (dayPart === '*') return ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
      if (dayPart === '?') return [];
      return dayPart.split(',').map(day => {
        if (day.length === 3) return day.toUpperCase();
        const dayMap = { 1: 'SUN', 2: 'MON', 3: 'TUE', 4: 'WED', 5: 'THU', 6: 'FRI', 7: 'SAT' };
        return dayMap[day] || day.toUpperCase();
      });
    }
    return [];
  }

  function extractTimesFromCron(awsCron) {
    const parts = awsCron
      .replace('cron(', '')
      .replace(')', '')
      .split(' ');
    if (parts.length >= 2) {
      const minutePart = parts[0];
      const hourPart = parts[1];

      if (hourPart.includes('*/')) {
        const interval = parseInt(hourPart.split('*/')[1]);
        const times = [];
        for (let h = 0; h < 24; h += interval) {
          const m = minutePart === '*' ? 0 : parseInt(minutePart);
          times.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
        }
        return times;
      }

      if (hourPart.includes(',')) {
        return hourPart.split(',').map(hour => {
          const h = parseInt(hour);
          const m = minutePart === '*' ? 0 : parseInt(minutePart);
          return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
        });
      }

      if (hourPart !== '*' && !hourPart.includes('/')) {
        const h = parseInt(hourPart);
        const m = minutePart === '*' ? 0 : parseInt(minutePart);
        return [`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`];
      }
    }
    return ['06:00'];
  }

  function validateSchedulingConfiguration() {
    if (schedulingMode === 'interval') {
      return timeUnit && timeValue && timeValue.value;
    } else if (schedulingMode === 'calendar') {
      if (schedules.length === 0) return false;
      return schedules.every(schedule => {
        const hasDays = schedule.days && schedule.days.length > 0;
        const hasTimes = schedule.times && schedule.times.length > 0;
        const validTimes = schedule.times.every(time => /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time));
        return hasDays && hasTimes && validTimes;
      });
    }
    return false;
  }

  async function setBugServiceFrequency() {
    let data;
    if (schedulingMode === 'interval') {
      data = { mode: 'interval', timeUnit, number: parseInt(timeValue.value) };
    } else if (schedulingMode === 'calendar') {
      data = {
        mode: 'calendar',
        schedules: schedules.map(schedule => ({ days: schedule.days, times: schedule.times }))
      };
    }

    if (vendorId.length) {
      try {
        await BZAPIService.postBugServiceSchedule(vendorId, data, { method: 'POST' });
        await getScheduleSettings();
      } catch (e) {
        console.log(e);
        throw e;
      }
    }
  }

  async function saveScheduleOnly() {
    if (!validateSchedulingConfiguration()) {
      toast.error('Please complete the scheduling configuration before saving.');
      return;
    }
    try {
      await setBugServiceFrequency();
      toast.success('Schedule saved successfully!');
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast.error('Failed to save schedule. Please try again.');
    }
  }

  async function forceRun() {
    if (!validateSchedulingConfiguration()) {
      toast.error('Please complete the scheduling configuration before saving.');
      return;
    }
    try {
      await setBugServiceFrequency();
      toast.success('Schedule saved successfully!');
      setModalOpen(!modalOpen);
      setIsForceRun(true);
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast.error('Failed to save schedule. Please try again.');
    }
  }

  function addNewSchedule() {
    setSchedules([...schedules, { days: [], times: ['06:00'] }]);
  }

  function updateSchedule(index, field, value) {
    const updatedSchedules = [...schedules];
    updatedSchedules[index] = { ...updatedSchedules[index], [field]: value };
    setSchedules(updatedSchedules);
  }

  function removeSchedule(index) {
    setSchedules(schedules.filter((_, i) => i !== index));
  }

  function addTimeToSchedule(scheduleIndex) {
    const updatedSchedules = [...schedules];
    updatedSchedules[scheduleIndex].times.push('06:00');
    setSchedules(updatedSchedules);
  }

  function updateTimeInSchedule(scheduleIndex, timeIndex, newTime) {
    const updatedSchedules = [...schedules];
    updatedSchedules[scheduleIndex].times[timeIndex] = newTime;
    setSchedules(updatedSchedules);
  }

  function removeTimeFromSchedule(scheduleIndex, timeIndex) {
    const updatedSchedules = [...schedules];
    if (updatedSchedules[scheduleIndex].times.length > 1) {
      updatedSchedules[scheduleIndex].times.splice(timeIndex, 1);
      setSchedules(updatedSchedules);
    }
  }

  if (isLoadingSchedule) {
    return (
      <div className="d-flex align-items-center justify-content-center py-4">
        <Spinner size="sm" className="me-2" />
        <span className="text-muted">Loading schedule...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Scheduling Mode Radio Buttons */}
      <div className="mb-3 d-flex align-items-center" style={{ gap: '20px' }}>
        <label
          className="d-flex align-items-center mb-0"
          style={{ cursor: 'pointer' }}
          onClick={() => setSchedulingMode('interval')}
        >
          <span
            style={{
              width: '18px',
              height: '18px',
              borderRadius: '50%',
              border: '2px solid #253746',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '8px'
            }}
          >
            {schedulingMode === 'interval' && (
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#253746' }} />
            )}
          </span>
          <span>Interval</span>
        </label>
        <label
          className="d-flex align-items-center mb-0"
          style={{ cursor: 'pointer' }}
          onClick={() => setSchedulingMode('calendar')}
        >
          <span
            style={{
              width: '18px',
              height: '18px',
              borderRadius: '50%',
              border: '2px solid #253746',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '8px'
            }}
          >
            {schedulingMode === 'calendar' && (
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#253746' }} />
            )}
          </span>
          <span>Calendar</span>
        </label>
      </div>

      {/* Interval Mode UI */}
      {schedulingMode === 'interval' && (
        <Row className="mb-3">
          <Col xs={6}>
            <Select
              options={timeUnitOptions}
              value={timeUnitOptions.find(el => el.value === timeUnit)}
              onChange={e => {
                if (e.value === 'minute') setTimeValue(mins[1]);
                else setTimeValue(hrs[3]);
                setTimeUnit(e.value);
              }}
              menuPortalTarget={document.body}
              styles={selectStyles}
              theme={selectTheme}
            />
          </Col>
          <Col xs={6}>
            <Select
              options={timeUnit === 'minute' ? mins : hrs}
              value={timeValue}
              placeholder="Select frequency"
              onChange={e => setTimeValue(e)}
              menuPortalTarget={document.body}
              styles={selectStyles}
              theme={selectTheme}
            />
          </Col>
        </Row>
      )}

      {/* Calendar Mode UI */}
      {schedulingMode === 'calendar' && (
        <div className="mb-3">
          {schedules.length === 0 && (
            <Button color="falcon-default" size="sm" onClick={addNewSchedule}>
              <FontAwesomeIcon icon="plus" className="me-1" /> Add Schedule
            </Button>
          )}

          {schedules.map((schedule, scheduleIndex) => (
            <div key={scheduleIndex} className="mb-3 p-2 border rounded">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="fw-bold">Schedule {scheduleIndex + 1}</small>
                {schedules.length > 1 && (
                  <Button color="falcon-danger" size="sm" onClick={() => removeSchedule(scheduleIndex)}>
                    <FontAwesomeIcon icon="trash" />
                  </Button>
                )}
              </div>
              <div className="mb-2">
                <small className="text-muted">Days:</small>
                <Select
                  isMulti
                  options={daysOfWeek}
                  value={daysOfWeek.filter(day => schedule.days.includes(day.value))}
                  onChange={selectedDays =>
                    updateSchedule(scheduleIndex, 'days', selectedDays ? selectedDays.map(d => d.value) : [])
                  }
                  placeholder="Select days"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectStyles}
                  theme={selectTheme}
                />
              </div>
              <div className="mb-2">
                <small className="text-muted">Times (UTC):</small>
                {schedule.times.map((time, timeIndex) => (
                  <div key={timeIndex} className="d-flex align-items-center mb-1">
                    <input
                      type="time"
                      value={time}
                      onChange={e => updateTimeInSchedule(scheduleIndex, timeIndex, e.target.value)}
                      className="form-control form-control-sm me-2"
                      style={{ width: '120px' }}
                    />
                    {schedule.times.length > 1 && (
                      <Button
                        color="falcon-danger"
                        size="sm"
                        onClick={() => removeTimeFromSchedule(scheduleIndex, timeIndex)}
                      >
                        <FontAwesomeIcon icon="times" />
                      </Button>
                    )}
                    {timeIndex === schedule.times.length - 1 && (
                      <Button
                        color="falcon-default"
                        size="sm"
                        onClick={() => addTimeToSchedule(scheduleIndex)}
                        className="ms-2"
                      >
                        <FontAwesomeIcon icon="plus" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {schedules.length > 0 && (
            <Button color="falcon-default" size="sm" onClick={addNewSchedule}>
              <FontAwesomeIcon icon="plus" className="me-1" /> Add Another Schedule
            </Button>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="d-flex justify-content-between mt-4">
        <Button color="falcon-default" onClick={() => saveScheduleOnly()} disabled={!validateSchedulingConfiguration()}>
          Save Schedule
        </Button>
        <Button
          color="falcon-default"
          onClick={() => forceRun()}
          disabled={!isConfigured || !validateSchedulingConfiguration()}
        >
          Save &amp; Run Now
        </Button>
      </div>
    </div>
  );
};

ScheduleAccordionContent.propTypes = {
  vendorId: PropTypes.string.isRequired,
  timeUnit: PropTypes.string,
  setTimeUnit: PropTypes.func.isRequired,
  timeValue: PropTypes.object,
  setTimeValue: PropTypes.func.isRequired,
  schedulingMode: PropTypes.string,
  setSchedulingMode: PropTypes.func.isRequired,
  schedules: PropTypes.array,
  setSchedules: PropTypes.func.isRequired,
  isConfigured: PropTypes.bool,
  modalOpen: PropTypes.bool,
  setModalOpen: PropTypes.func,
  setIsForceRun: PropTypes.func,
  onLoaded: PropTypes.func
};

export default ScheduleAccordionContent;
